import React, { useState, useEffect, useCallback, useRef } from 'react'
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom'
import { MantineProvider } from '@mantine/core'
import { DataCollection } from './components/DataCollection'
import { Admin } from './components/Admin'
import { PrizeWheel } from './components/PrizeWheel'
import { SocketProvider, useSocket } from './contexts/SocketContext'
import { ThemedDataCollection } from './components/ThemedDataCollection'
import SitehoundPrizeWheel from './components/SitehoundPrizeWheel'
import { ThemeProvider } from './contexts/ThemeContext'
import { PrizeDisplay } from './components/PrizeDisplay'
import './global.css'
import './prizedisplay.css'

const INACTIVITY_TIMEOUT = 12000*10 // 20 seconds in milliseconds

function WheelContent() {
  const [showScreensaver, setShowScreensaver] = useState(false)
  const [prizes, setPrizes] = useState([])
  const inactivityTimerRef = useRef(null)
  const socket = useSocket()
  const [spinTrigger1, setSpinTrigger1] = useState(null)
  const [spinTrigger2, setSpinTrigger2] = useState(null)
  const prizesRef = useRef([])

  const showScreensaverOn = useCallback(() => {
    setShowScreensaver(true)
    console.log('Screensaver turned on')
  }, [])

  const showScreensaverOff = useCallback(() => {
    setShowScreensaver(false)
    console.log('Screensaver turned off')
  }, [])

  const resetInactivityTimer = useCallback(() => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current)
    }
    inactivityTimerRef.current = setTimeout(showScreensaverOn, INACTIVITY_TIMEOUT)
  }, [showScreensaverOn])

  const toggleScreensaver = useCallback(() => {
    if (showScreensaver) {
      showScreensaverOff()
      resetInactivityTimer()
    } else {
      showScreensaverOn()
    }
  }, [showScreensaver, showScreensaverOff, showScreensaverOn, resetInactivityTimer])

  useEffect(() => {
    if (socket) {
      console.log("Socket connected, setting up prize listeners")
      
      const onPrizes = (receivedPrizes) => {
        console.log("Received prizes:", receivedPrizes)
        if (Array.isArray(receivedPrizes)) {
          setPrizes(receivedPrizes)
          prizesRef.current = receivedPrizes
        } else {
          console.warn("Received invalid prizes data:", receivedPrizes)
          setPrizes([])
        }
      }

      const refreshPrizes = () => {
        console.log("Requesting fresh prizes from server")
        socket.emit('get_prizes')
      }

      const onEventDateChanged = ({ currentDate, timezone, prizeCount }) => {
        console.log(`Event date changed: ${currentDate} (${timezone}), prizeCount: ${prizeCount}`)
        refreshPrizes()
      }

      const onTimezoneChanged = (data) => {
        console.log('Timezone changed:', data)
        refreshPrizes()
      }

      const onConnect = () => {
        console.log("Socket connected, requesting prizes")
        refreshPrizes()
      }

      const onError = (error) => {
        console.error("Socket error:", error)
      }

      const onSpinResult = ({ prize, email, instance: resultInstance, prizeIndex, eventDate }) => {
        console.log('Received spin_result:', JSON.stringify(prize), 'for instance:', resultInstance, 'eventDate:', eventDate, 'prizeIndex:', prizeIndex);
        showScreensaverOff();
        
        const resultInstanceInt = parseInt(resultInstance, 10);
        if (isNaN(resultInstanceInt)) {
          console.error('Instance is not a number:', resultInstance);
          return;
        }

        let resolvedIndex = typeof prizeIndex === 'number' && prizeIndex >= 0 ? prizeIndex : -1;
        if (resolvedIndex === -1) {
          resolvedIndex = prizesRef.current.findIndex((p) => 
            (p.name || '').trim().toLowerCase() === (prize.name || '').trim().toLowerCase()
          );
        }

        if (resolvedIndex !== -1) {
          const spinTrigger = { prizeIndex: resolvedIndex, prize };
          if (resultInstanceInt === 1) {
            setSpinTrigger1(spinTrigger);
          } else if (resultInstanceInt === 2) {
            setSpinTrigger2(spinTrigger);
          }
        } else {
          console.error('Received prize not found in prizes array, refreshing prizes:', prize);
          refreshPrizes();
        }
      }

      // Add connect and error listeners
      socket.on('connect', onConnect)
      socket.on('error', onError)
      socket.on('prizes', onPrizes)
      socket.on('spin_result', onSpinResult)
      socket.on('event_date_changed', onEventDateChanged)
      socket.on('timezone_changed', onTimezoneChanged)

      const refreshInterval = setInterval(refreshPrizes, 60000)
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          refreshPrizes()
        }
      }
      document.addEventListener('visibilitychange', onVisibilityChange)

      // Request prizes immediately if socket is already connected
      if (socket.connected) {
        console.log("Socket already connected, requesting prizes")
        refreshPrizes()
      }

      return () => {
        clearInterval(refreshInterval)
        document.removeEventListener('visibilitychange', onVisibilityChange)
        socket.off('connect', onConnect)
        socket.off('error', onError)
        socket.off('prizes', onPrizes)
        socket.off('spin_result', onSpinResult)
        socket.off('event_date_changed', onEventDateChanged)
        socket.off('timezone_changed', onTimezoneChanged)
      }
    } else {
      console.warn("Socket not available")
    }
  }, [socket, showScreensaverOff])

  useEffect(() => {
    resetInactivityTimer()
    window.toggleScreensaver = toggleScreensaver
    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current)
      }
      delete window.toggleScreensaver
    }
  }, [resetInactivityTimer, toggleScreensaver])

  return (
    <div style={{ position: 'relative', width: '100vw', height: '100vh', overflow: 'hidden' }}>
      <div style={{ display: 'flex', width: '100%', height: '100%' }}>
        <PrizeWheel 
          instance={1} 
          prizes={prizes}
          spinTrigger={spinTrigger1}
          onResetInactivity={resetInactivityTimer}
          onScreensaverOff={showScreensaverOff}
        />
        <PrizeWheel 
          instance={2} 
          prizes={prizes}
          spinTrigger={spinTrigger2}
          onResetInactivity={resetInactivityTimer}
          onScreensaverOff={showScreensaverOff}
        />
      </div>
      <PrizeDisplay prizes={prizes} isVisible={showScreensaver} />
    </div>
  )
}

// Sitehound: simple wheel screen (fetches prizes via socket and renders simplified wheel)
function SitehoundWheelScreen() {
  const socket = useSocket()
  const [prizes, setPrizes] = useState([])

  useEffect(() => {
    if (!socket) return

    const onPrizes = (receivedPrizes) => {
      if (Array.isArray(receivedPrizes)) setPrizes(receivedPrizes)
    }
    const onConnect = () => socket.emit('get_prizes')

    socket.on('connect', onConnect)
    socket.on('prizes', onPrizes)
    if (socket.connected) socket.emit('get_prizes')

    return () => {
      socket.off('connect', onConnect)
      socket.off('prizes', onPrizes)
    }
  }, [socket])

  return (
    <ThemeProvider tenant='sitehound'>
      <SitehoundPrizeWheel prizes={prizes} />
    </ThemeProvider>
  )
}

// Sitehound: data collection screen with Sitehound theme
function SitehoundDataCollectionPage() {
  return (
    <ThemeProvider tenant='sitehound'>
      <ThemedDataCollection instance='sitehound' />
    </ThemeProvider>
  )
}

export default function App() {
  return (
    <MantineProvider>
      <SocketProvider>
        <Router>
          <Routes>
            <Route path="/" element={<DataCollection instance={1} />} />
            <Route path="/1" element={<DataCollection instance={1} />} />
            <Route path="/2" element={<DataCollection instance={2} />} />
            <Route path="/wheel" element={<WheelContent />} />
            {/* Sitehound routes (simplified themed experience) */}
            <Route path="/sitehound" element={<SitehoundDataCollectionPage />} />
            <Route path="/sitehound/wheel" element={<SitehoundWheelScreen />} />
            <Route path="/sitehound/admin" element={<Admin />} />
            <Route path="/admin" element={<Admin />} />
          </Routes>
        </Router>
      </SocketProvider>
    </MantineProvider>
  )
}
